import { useCallback, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AsyncOperationProgress } from "@/components/progress";
import { useAsyncOperationProgress } from "@/components/progress";
import { cn } from "@/lib/utils";

interface QuestionnaireAttachmentProgressDialogProps {
  open: boolean;
  operationProgress: ReturnType<typeof useAsyncOperationProgress>;
  currentItemLabel?: string;
  translationKey: string;
  onClose: (open?: boolean) => void;
  /**
   * Element, który ma dostać fokus po zamknięciu okna. Radix wraca do przycisku-wyzwalacza,
   * a ten mógł zniknąć (np. „Usuń" skanu po jego usunięciu) - wtedy fokus spadłby na <body>.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /**
   * W trakcie przetwarzania Esc, klik poza oknem i „X" nic nie robią (przycisk „X" jest ukryty):
   * zamknięte okno postępu nie ma jak wrócić, a wsad i tak trwa dalej. Domyślnie wyłączone (ankieta).
   */
  preventCloseWhileBusy?: boolean;
}

export function QuestionnaireAttachmentProgressDialog({
  open,
  operationProgress,
  currentItemLabel,
  translationKey,
  onClose,
  returnFocusRef,
  preventCloseWhileBusy = false,
}: QuestionnaireAttachmentProgressDialogProps) {
  const { t } = useTranslation();
  const lockWhileBusy = preventCloseWhileBusy && operationProgress.isBusy();

  const handleAsyncClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && lockWhileBusy) return;
      onClose(nextOpen);
    },
    [lockWhileBusy, onClose],
  );

  const handleCloseAutoFocus = useCallback(
    (event: Event) => {
      const target = returnFocusRef?.current;
      if (!target) return;
      event.preventDefault();
      target.focus();
    },
    [returnFocusRef],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "sm:max-w-4xl max-h-[90vh] flex flex-col",
          lockWhileBusy && "[&>button:last-child]:hidden",
        )}
        aria-describedby={undefined}
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        {/* Nazwa okna dla czytników ekranu (Radix wymaga DialogTitle); widoczny tytuł niesie karta postępu. */}
        <DialogTitle className="sr-only">
          {t("fileAttachments.progressDialogTitle")}
        </DialogTitle>
        <AsyncOperationProgress
          operationProgress={operationProgress}
          currentItemLabel={currentItemLabel}
          onClose={handleAsyncClose}
          translationKey={translationKey}
        />
      </DialogContent>
    </Dialog>
  );
}
